import type { FormField } from '@nexofacil/schemas';
import { CheckCircle2, Home as HomeIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { SelectCard } from '@/components/form/select-card';
import { Stepper } from '@/components/form/stepper';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  formatBrazilianPhone,
  isPhoneInputComplete,
  isSuspiciousBrPhone,
} from '@/lib/phone-mask';
import { cn } from '@/lib/utils';
import { getFormIcon } from './icon-registry';

// =============================================================================
// DynamicField · renders one FormField given the current react-hook-form
// instance. Knows how to translate a config-declared field type into the
// existing shadcn/tailwind primitives (SelectCard, Stepper, Input).
//
// Visibility: caller is responsible for not rendering hidden fields (this
// keeps DynamicField focused on rendering). DynamicForm filters by
// isFieldVisible before mapping.
//
// Path: the form path is computed by the caller · `lead` fields are stored at
// the top level (form.register(field.id)) and `answers` fields nested under
// `answers.{id}`. This keeps the payload shape stable for the API.
// =============================================================================

type Form = UseFormReturn<Record<string, unknown>>;

type DynamicFieldProps = {
  field: FormField;
  form: Form;
  /** Read value at field path. */
  value: unknown;
  /** Sets value + triggers validation. */
  setValue: (next: unknown) => void;
  /** Validation error message for this field, if any. */
  error?: string;
};

export function DynamicField({ field, form, value, setValue, error }: DynamicFieldProps) {
  const cols = field.columns ?? 1;
  const isYesNo = field.type === 'yes-no';
  const isChoice = field.type === 'single-choice' || field.type === 'money-bracket';

  const labelStyle = isCardField(field.type)
    ? 'block text-base font-semibold'
    : 'text-sm font-medium';

  return (
    <div className={cn(isCardField(field.type) ? 'space-y-3' : 'space-y-2')}>
      {/* htmlFor só nos campos de digitar: liga rótulo e campo pro leitor de tela e pro autofill. */}
      <Label
        htmlFor={isCardField(field.type) ? undefined : fieldPath(field)}
        className={labelStyle}
      >
        {field.label}
      </Label>

      {/* The actual control */}
      {field.type === 'text' && (
        <TextLikeInput
          path={fieldPath(field)}
          form={form}
          placeholder={field.placeholder}
          autoFocus={false}
          autoComplete={field.id === 'name' ? 'name' : undefined}
        />
      )}

      {field.type === 'email' && (
        <TextLikeInput
          path={fieldPath(field)}
          form={form}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={field.placeholder ?? 'voce@email.com'}
        />
      )}

      {field.type === 'phone' && (
        <PhoneInput path={fieldPath(field)} form={form} placeholder={field.placeholder} />
      )}

      {field.type === 'number' && (
        <Input
          {...form.register(fieldPath(field), { valueAsNumber: true })}
          id={fieldPath(field)}
          type="number"
          inputMode="numeric"
          min={field.validation?.min}
          max={field.validation?.max}
          className="h-12 max-w-[12rem] text-base"
          placeholder={field.placeholder}
        />
      )}

      {field.type === 'stepper' && (
        <Stepper
          value={(value as number) ?? field.validation?.min ?? 0}
          onChange={(v) => setValue(v)}
          min={field.validation?.min ?? 0}
          max={field.validation?.max ?? 99}
        />
      )}

      {isYesNo && (
        <CardsGrid cols={2}>
          <SelectCard
            label="Sim"
            icon={CheckCircle2}
            selected={value === true}
            onClick={() => setValue(true)}
          />
          <SelectCard
            label="Não"
            icon={HomeIcon}
            selected={value === false}
            onClick={() => setValue(false)}
          />
        </CardsGrid>
      )}

      {isChoice && field.options && (
        <CardsGrid cols={cols}>
          {field.options.map((opt) => {
            const Icon = getFormIcon(opt.iconName);
            const isSelected = optionsEqual(value, opt.value);
            return (
              <SelectCard
                key={String(opt.value ?? 'null')}
                label={opt.label}
                description={opt.description}
                icon={Icon}
                selected={isSelected}
                onClick={() => setValue(opt.value)}
              />
            );
          })}
        </CardsGrid>
      )}

      {field.hint && !error && (
        <p className="text-xs leading-relaxed text-muted-foreground">{field.hint}</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small helpers
// -----------------------------------------------------------------------------

function isCardField(t: FormField['type']): boolean {
  return t === 'single-choice' || t === 'money-bracket' || t === 'yes-no' || t === 'stepper';
}

/** Returns the dotted path under which a field's value lives in the form state. */
export function fieldPath(field: FormField): string {
  const scope = field.scope ?? 'answers';
  return scope === 'lead' ? field.id : `answers.${field.id}`;
}

/** money-bracket can carry null; compare with null-aware equality. */
function optionsEqual(a: unknown, b: unknown): boolean {
  if (a === undefined && b === null) return false;
  if (a === null && b === undefined) return false;
  return a === b;
}

function CardsGrid({ children, cols = 1 }: { children: ReactNode; cols?: 1 | 2 | 3 }) {
  const grid =
    cols === 3
      ? 'grid grid-cols-1 gap-3 sm:grid-cols-3'
      : cols === 2
        ? 'grid grid-cols-1 gap-3 sm:grid-cols-2'
        : 'grid grid-cols-1 gap-3';
  return <div className={grid}>{children}</div>;
}

function TextLikeInput({
  path,
  form,
  type,
  inputMode,
  placeholder,
  autoFocus,
  autoComplete,
}: {
  path: string;
  form: Form;
  type?: 'text' | 'email';
  inputMode?: 'text' | 'email';
  placeholder?: string;
  autoFocus?: boolean;
  autoComplete?: string;
}) {
  return (
    <Input
      {...form.register(path)}
      id={path}
      className="h-12 text-base"
      type={type ?? 'text'}
      inputMode={inputMode}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
    />
  );
}

function PhoneInput({
  path,
  form,
  placeholder,
}: {
  path: string;
  form: Form;
  placeholder?: string;
}) {
  const phoneReg = form.register(path, {
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatBrazilianPhone(e.target.value);
      form.setValue(path, formatted, { shouldValidate: false });
    },
  });
  const current = (form.watch(path) as string | undefined) ?? '';
  const incomplete = current && !isPhoneInputComplete(current);
  const suspicious = !incomplete && isSuspiciousBrPhone(current);

  return (
    <div>
      <Input
        {...phoneReg}
        id={path}
        className="h-12 text-base"
        type="tel"
        placeholder={placeholder ?? '(48) 99999-9999'}
        autoComplete="tel"
        inputMode="numeric"
        maxLength={16}
      />
      {incomplete && (
        <p className="mt-1 text-xs text-muted-foreground">
          Digite o número completo com DDD
        </p>
      )}
      {suspicious && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
          Parece que o código do país entrou como DDD · celular sempre começa
          com 9 (ex: 48 99999-9999). Confirme se o número está certo.
        </p>
      )}
    </div>
  );
}
